import { UploadOutlined } from "@ant-design/icons"
import { type AsyncThunk, type AsyncThunkConfig } from "@reduxjs/toolkit"
import { Button, Tooltip, Upload, type UploadProps } from "antd"
import { type AxiosProgressEvent } from "axios"
import React, { type ReactNode, useState } from "react"

import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { validateFile } from "../../../../../utils/file-validation.ts"
import { MAX_SIZE_MB } from "../../../LeadWorkspacePage/lead-attachments.ts"

const ALLOWED_FILE_EXTENSIONS = ["pdf", "docx", "doc"]

type DocumentType = "proposal" | "contract"

interface LeadViewDocumentUploadRenderState {
  loading: boolean
  progress: number
}

interface LeadViewDocumentUploadProps<T> {
  leadId: string
  documentType: DocumentType
  label?: string
  block?: boolean
  /** Visual only — same upload mechanism either way. */
  outlined?: boolean
  /** Replaces the default button entirely (e.g. to embed this as a dropdown row) — the click still opens the same file picker and runs the same upload. */
  render?: (state: LeadViewDocumentUploadRenderState) => ReactNode
  uploadThunk: AsyncThunk<
    T,
    {
      file: File
      lead_id: string
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
    },
    AsyncThunkConfig
  >
}

const DOCUMENT_CONFIG: Record<DocumentType, { tooltip: string }> = {
  proposal: { tooltip: "Upload Proposal" },
  contract: { tooltip: "Upload Contract" }
}

export function LeadViewDocumentUpload<T>({
  leadId,
  documentType,
  label = "Upload",
  block,
  outlined,
  render,
  uploadThunk
}: LeadViewDocumentUploadProps<T>) {
  const dispatch = useAppDispatch()

  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  const config = DOCUMENT_CONFIG[documentType]

  const props: UploadProps = {
    name: "file",
    customRequest: async ({ file, onSuccess, onProgress }) => {
      setIsUploading(true)
      setProgress(0)

      try {
        const response = await dispatch(
          uploadThunk({
            file: file as File,
            lead_id: leadId,
            onUploadProgress: (e: AxiosProgressEvent) => {
              const percent = Math.round((e?.loaded / (e?.total || 1)) * 100)
              setProgress(percent)
              onProgress?.({ percent })
            }
          })
        ).unwrap()

        onSuccess?.(response)
      } finally {
        setIsUploading(false)
      }
    },
    multiple: false,
    maxCount: 1,
    accept: ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    showUploadList: false,
    beforeUpload: (file) => {
      return validateFile(file, {
        maxSize: MAX_SIZE_MB,
        allowedExtensions: ALLOWED_FILE_EXTENSIONS,
        defaultResult: true
      })
    }
  }

  return (
    <Upload {...props} className={block ? "block [&_.ant-upload]:w-full" : ""}>
      {render ? (
        render({ loading: isUploading, progress })
      ) : (
        <Tooltip title={config.tooltip}>
          <Button
            block={block}
            variant={outlined ? "outlined" : "solid"}
            color="primary"
            icon={<UploadOutlined />}
            loading={isUploading}
            disabled={isUploading}
          >
            {isUploading ? `Uploading ${progress}%` : label}
          </Button>
        </Tooltip>
      )}
    </Upload>
  )
}
